import * as z from "zod";
import { Id, IsoDate, RunStatus } from "./ids.js";

export const LIVE_VOICE_CONTEXT_DEFAULTS = {
  triggerTokens: 16_000,
  targetTokens: 12_000,
  summaryMaxTokens: 4_000,
} as const;

export const LIVE_AGENT_ANSWER_MAX_LENGTH = 8192;
export const LIVE_AGENT_CONTEXT_INSTRUCTIONS_LENGTH = 1600;
export const LIVE_AGENT_CONTEXT_HISTORY_LENGTH = 600;
export const LIVE_AGENT_CONTEXT_HISTORY_COUNT = 4;
export const LIVE_AGENT_PREVIOUS_CONVERSATIONS = 3;
export const LIVE_AGENT_RECALL_PAGE_SIZE = 3;

const liveCallId = z.string().regex(/^[A-Za-z0-9_-]{1,96}$/);
const liveHistoryTurn = z.object({
  role: z.enum(["user", "bot"]),
  text: z.string().max(4000),
  seq: z.number().int().min(0),
  createdAt: IsoDate,
  interrupted: z.boolean().optional(),
});
export const LiveAgentConversationSchema = z.object({
  callId: liveCallId,
  startedAt: IsoDate,
  updatedAt: IsoDate,
  excerpts: z.array(liveHistoryTurn.extend({ text: z.string().max(600) })).max(3),
});
export const LiveAgentRecallInputSchema = z
  .object({
    currentCallId: liveCallId,
    callId: liveCallId.optional(),
    conversation: z.number().int().min(0).max(LIVE_AGENT_PREVIOUS_CONVERSATIONS).default(0),
    beforeSeq: z.number().int().min(0).optional(),
  })
  .refine((input) => input.beforeSeq === undefined || Boolean(input.callId), {
    message: "Paging requires a stable callId / Сторінки потребують сталого ідентифікатора розмови",
    path: ["callId"],
  });
export const LiveAgentRecallSchema = z.object({
  callId: liveCallId.nullable(),
  conversation: z.number().int().min(0).max(LIVE_AGENT_PREVIOUS_CONVERSATIONS),
  found: z.boolean(),
  turns: z.array(liveHistoryTurn).max(LIVE_AGENT_RECALL_PAGE_SIZE),
  nextBeforeSeq: z.number().int().min(0).nullable(),
  conversations: z.array(LiveAgentConversationSchema).max(LIVE_AGENT_PREVIOUS_CONVERSATIONS),
});
export type LiveAgentRecall = z.infer<typeof LiveAgentRecallSchema>;
export type LiveAgentRecallInput = z.infer<typeof LiveAgentRecallInputSchema>;
export type LiveAgentConversation = z.infer<typeof LiveAgentConversationSchema>;

/** What an active run is doing now, so the voice can say more than "still working".
 * Що робить активний запуск зараз, щоб голос казав більше, ніж «ще працюю». */
export const LiveAgentActivitySchema = z.object({
  // Humanized name of the latest tool step; null before any tool ran.
  label: z.string().max(60).nullable(),
  // Tool steps in the recent messages of the run, a lower bound for long runs.
  steps: z.number().int().min(0),
  elapsedSeconds: z.number().int().min(0).nullable(),
});
export type LiveAgentActivity = z.infer<typeof LiveAgentActivitySchema>;

export const LiveAgentTaskSchema = z.object({
  taskId: Id,
  runId: Id,
  status: RunStatus,
  answer: z.string().max(LIVE_AGENT_ANSWER_MAX_LENGTH).nullable(),
  answerTruncated: z.boolean(),
  // Identity survives status changes. / Ідентичність відповіді зберігається при зміні стану.
  answerMessageId: Id.optional(),
  terminationUnconfirmed: z.boolean().optional(),
  // Added after the first release: older servers omit it and older clients ignore it.
  activity: LiveAgentActivitySchema.optional(),
});
export type LiveAgentTask = z.infer<typeof LiveAgentTaskSchema>;

export const LiveAgentTaskStateSchema = z.object({
  threadId: Id,
  cursor: z.number().int().min(-1),
  task: LiveAgentTaskSchema.nullable(),
});
export type LiveAgentTaskState = z.infer<typeof LiveAgentTaskStateSchema>;

export const LiveAgentContextSchema = z.object({
  spaceId: Id.optional(),
  botName: z.string().max(180),
  instructions: z.string().max(LIVE_AGENT_CONTEXT_INSTRUCTIONS_LENGTH),
  history: z
    .array(
      z.object({
        role: z.enum(["user", "bot"]),
        text: z.string().max(LIVE_AGENT_CONTEXT_HISTORY_LENGTH),
        createdAt: IsoDate,
      }),
    )
    .max(LIVE_AGENT_CONTEXT_HISTORY_COUNT),
  conversations: z
    .array(LiveAgentConversationSchema)
    .max(LIVE_AGENT_PREVIOUS_CONVERSATIONS)
    .optional(),
  tasks: z.array(LiveAgentTaskSchema.omit({ answer: true, answerTruncated: true })).max(20),
  asOf: IsoDate,
});
export type LiveAgentContext = z.infer<typeof LiveAgentContextSchema>;

export const LiveAgentRecordSchema = z.object({
  callId: z.string().regex(/^[A-Za-z0-9_-]{1,96}$/),
  turn: z.number().int().min(0).max(9999),
  role: z.enum(["user", "bot"]),
  // A chunk can contain only whitespace; trimming it would corrupt a long transcript.
  // Частина може містити лише пробіли; їх обрізання пошкодило б довгу репліку.
  text: z.string().min(1).max(4000),
  interrupted: z.boolean().optional(),
});
export type LiveAgentRecord = z.infer<typeof LiveAgentRecordSchema>;
